"use client";

import { markAgeGroupFromBirthYear } from "@/lib/age-group/birth-year-store";
import { isGrownupFromStorage } from "@/lib/age-group/store";
import { isAdultBirthYear } from "@/lib/utils/adult-age";
import type React from "react";
import { useCallback, useEffect, useState } from "react";

export type ContactFormState = {
  name: string;
  email: string;
  message: string;
  birthYear: string;
  optIn: boolean;
  website: string; // Honeypot
};

export type UseContactFormReturn = ContactFormState & {
  setName: (v: string) => void;
  setEmail: (v: string) => void;
  setMessage: (v: string) => void;
  setBirthYear: (v: string) => void;
  setOptIn: (v: boolean) => void;
  setWebsite: (v: string) => void;
  /** False when already marked grownup — hide the birth-year field. */
  needsBirthYear: boolean;
  loading: boolean;
  submitted: boolean;
  handleSubmit: (e: React.FormEvent) => Promise<void>;
};

const ENDPOINT = "/api/feedback";

async function readErrorDetail(response: Response): Promise<unknown> {
  try {
    return await response.json();
  } catch {
    try {
      return await response.text();
    } catch {
      return undefined;
    }
  }
}

export function useContactForm(): UseContactFormReturn {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [message, setMessage] = useState("");
  const [birthYear, setBirthYear] = useState("");
  // Stable SSR default (true). Storage is client-only — sync after mount to avoid hydration mismatch.
  const [needsBirthYear, setNeedsBirthYear] = useState(true);
  const [optIn, setOptIn] = useState(true);
  const [website, setWebsite] = useState(""); // Honeypot
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setNeedsBirthYear(!isGrownupFromStorage());
  }, []);

  const handleSubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();
      setLoading(true);
      try {
        // Only ask/send birth year when we don't already know they're a grownup.
        // Year is sent with the form when present, but never persisted — only kid/grownup is.
        const yearToSend = needsBirthYear ? birthYear : "";

        if (yearToSend) {
          // Under-18: persist kid without notifying so ContactAccessGate does not
          // redirect before the soft thank-you can render. Cookie still blocks
          // later /contact loads via middleware.
          const notify = isAdultBirthYear(yearToSend);
          markAgeGroupFromBirthYear(yearToSend, { notify });
        }

        const response = await fetch(ENDPOINT, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            name,
            email,
            message,
            ...(yearToSend ? { birthYear: yearToSend } : {}),
            optIn,
            website, // honeypot
          }),
        });
        if (!response.ok) {
          const errorDetail = await readErrorDetail(response);
          // Include info useful for debugging in the program output
          // eslint-disable-next-line no-console
          console.error("Contact form submission failed", {
            status: response.status,
            statusText: response.statusText,
            error: errorDetail,
          });
        }
        // Always show thank you, regardless of response
        setSubmitted(true);
        setName("");
        setEmail("");
        setMessage("");
        setBirthYear("");
        setOptIn(true);
        setWebsite("");
      } catch {
        setSubmitted(true); // Still show thank you
      } finally {
        setLoading(false);
      }
    },
    [name, email, message, birthYear, needsBirthYear, optIn, website]
  );

  return {
    name,
    email,
    message,
    birthYear,
    optIn,
    website,
    setName,
    setEmail,
    setMessage,
    setBirthYear,
    setOptIn,
    setWebsite,
    needsBirthYear,
    loading,
    submitted,
    handleSubmit,
  };
}
